import React from 'react'

import InputTextarea from '../../components/InputTextarea'

import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useViewport from '../../modules/viewport/useViewport'
import useOpenEndedLogger from '../../modules/supervisor/useOpenEndedLogger'
import useFirstMountEffect from '@aytm/lib/hooks/useFirstMountEffect'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'

import { IQuestionViewProps } from '../types'
import { OpenEndedQuestionModelTypes } from './types'

import commonStyle from '../_common/style.module.scss'
import style from './style.module.scss'
import { hapticEffects } from '../../lib/hapticEffects'

const onCommentFocus = () => hapticEffects.soft()

const OpenEndedView = ({
  schema,
  response,
  responseValidation,
  timeline,
  showValidation,
  onUpdateResponse
}: IQuestionViewProps<OpenEndedQuestionModelTypes>) => {
  const onCommentChange = (value: string) => {
    onUpdateResponse({ ...response, comment: value.slice(0, 5000) })
  }

  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const min = schema.min_characters || 0
  const max = schema.max_characters || 5000
  const optional = schema.optional
  const note = t<(min: number, max: number, optional: boolean) => string>('questions', 'oe', 'note')
  const noteText = schema.validation_message || note(min, max, optional)
  const { localeOptions } = useInterfaceLocale()

  const openEndedLogger = useOpenEndedLogger(timeline, schema.id)
  useFirstMountEffect(() => void openEndedLogger(response.comment, schema.id, false))
  const onImmediateChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    openEndedLogger(event.target.value, schema.id)
  }

  return (
    <fieldset
      className={`${style.openEndedView} ${localeOptions.rtl ? style.openEndedViewRTL : ''}`}
      data-testid="SW-OE"
    >
      {noteText && (
        <legend
          data-testid="SW-OE-ValidationMessage"
          className={`${commonStyle.validationMessage} ${style.validationMessage} ${
            showValidation && !responseValidation.valid ? commonStyle.error : ''
          } `}
          dangerouslySetInnerHTML={{ __html: noteText }}
        />
      )}
      <label className={style.label} htmlFor={schema.id} aria-label={schema.text}>
        <InputTextarea
          id={schema.id}
          aria-label={schema.text}
          aria-required={!optional}
          className={`${style.textarea} ${showValidation && !responseValidation.valid ? style.error : ''}`}
          placeholder={t('questions', 'placeholder')}
          rows={6}
          autoResize={true}
          autoFocus={true}
          debounce={viewport.mobile ? 500 : 300}
          value={response.comment}
          data-testid="SW-OE-Input"
          onChange={onImmediateChange}
          onInputChange={onCommentChange}
          onFocus={onCommentFocus}
        />
      </label>
    </fieldset>
  )
}

export default OpenEndedView
